<template>
  <node-view-wrapper as="span" class="mention mention--ref ref-mention-node-view">
    <v-menu open-on-hover bottom offset-y :close-on-content-click="false" max-width="400" open-delay="300">
      <template #activator="{ on, attrs }">
        <span v-bind="attrs" v-on="on" class="ref-mention-chip" @click.stop="editor && !editor.isEditable && navigate()"
          >{{ displayLabel }}</span
        >
      </template>
      <v-card v-if="resolvedItem" class="ref-mention-preview" max-width="400">
        <v-list class="py-0">
          <refs-ticket v-if="isTicket" :item="resolvedItem" @openDetail="navigate">
            <template #icon>
              <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
            </template>
          </refs-ticket>
          <refs-report v-else-if="isReport" :item="resolvedItem" @openDetail="navigate">
            <template #icon>
              <v-icon class="mr-2">mdi-car-wrench</v-icon>
            </template>
          </refs-report>
          <refs-vehicle v-else-if="isVehicle" :item="resolvedItem" @openDetail="navigate">
            <template #icon>
              <v-icon class="mr-2">mdi-car</v-icon>
            </template>
          </refs-vehicle>
          <refs-thg v-else-if="isThg" :item="resolvedItem" @openDetail="navigate">
            <template #icon>
              <v-icon class="mr-2">mdi-certificate</v-icon>
            </template>
          </refs-thg>
        </v-list>
      </v-card>
      <v-card v-else class="ref-mention-preview pa-3" max-width="400">
        <v-progress-circular v-if="isLoading" indeterminate size="20" width="2" class="mr-2" />
        <span v-else-if="isErrored && isNotFound" class="text-caption grey--text">
          {{ $t("sign.Sign.error.404") }}
        </span>
        <span v-else-if="isErrored" class="text-caption grey--text"> {{ $t("loadingError") }} </span>

        <span v-else class="text-caption grey--text">Preview unavailable</span>
      </v-card>
    </v-menu>
  </node-view-wrapper>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { NodeViewWrapper, nodeViewProps } from "@tiptap/vue-2";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerModule } from "@/store/modules/partner";
import { TicketDataAccessLayer } from "@/store/modules/access-layers/ticket.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { VehicleAccessLayer } from "@/store/modules/access-layers/vehicle.access-layer";
import { ThgDataAccessLayer } from "@/store/modules/access-layers/thg.access-layer";
import { Ticket } from "@/models/ticket.entity";
import { Report } from "@/models/report.entity";
import { Vehicle } from "@/models/vehicle.entity";
import { Thg } from "@/models/thg.entity";
import thgService from "@/services/thg/services/thgService";
import { TicketRouteNames } from "@/lib/utility/ticket.go-to-helper";
import { ReportRouteNames } from "@/lib/utility/report.go-to-helper";
import RefsTicket from "@/components/utility/RefsTicket.vue";
import RefsReport from "@/components/utility/RefsReport.vue";
import RefsVehicle from "@/components/utility/RefsVehicle.vue";
import RefsThg from "@/components/utility/RefsThg.vue";
import { NotFoundException } from "@/lib/exceptions/http";

@Component({
  components: { NodeViewWrapper, RefsTicket, RefsReport, RefsVehicle, RefsThg },
  props: nodeViewProps
})
export default class RefMentionNodeView extends Vue {
  node!: any;
  editor!: any;

  resolvedItem: any = null;
  isLoading = false;
  hasFetched = false;
  isErrored = false;
  error: Error | unknown | null = null;

  get partnerId(): string {
    return PartnerModule.partner._id || PartnerModule.partner.id || "";
  }

  get isTicket(): boolean {
    return this.node.attrs.refType === BackendResourceEnum.TICKET;
  }

  get isReport(): boolean {
    return this.node.attrs.refType === BackendResourceEnum.REPORT;
  }

  get isVehicle(): boolean {
    return this.node.attrs.refType === BackendResourceEnum.VEHICLE;
  }

  get isThg(): boolean {
    return this.node.attrs.refType === BackendResourceEnum.THG;
  }

  get displayLabel(): string {
    const raw = this.node.attrs.label || this.node.attrs.id || "";
    if (this.isTicket && !String(raw).startsWith("#")) {
      return `#${raw}`;
    }
    return String(raw);
  }

  get isNotFound() {
    return this.error instanceof NotFoundException;
  }

  mounted() {
    this.tryResolveFromCache();
  }

  @Watch("node.attrs.id")
  onIdChange() {
    this.hasFetched = false;
    this.resolvedItem = null;
    this.isErrored = false;
    this.error = null;
    this.tryResolveFromCache();
  }

  tryResolveFromCache() {
    const { id, refType } = this.node.attrs;
    if (!id) return;

    if (refType === BackendResourceEnum.TICKET) {
      this.resolvedItem = TicketDataAccessLayer.get(id) ?? null;
    } else if (refType === BackendResourceEnum.REPORT) {
      this.resolvedItem = ReportDataAccessLayer.get(id) ?? null;
    } else if (refType === BackendResourceEnum.VEHICLE) {
      this.resolvedItem = VehicleAccessLayer.get(id) ?? null;
    } else if (refType === BackendResourceEnum.THG) {
      this.resolvedItem = ThgDataAccessLayer.get(id) ?? null;
    }

    if (!this.resolvedItem && !this.hasFetched) {
      this.fetchItem();
    }
  }

  async fetchItem() {
    const { id, refType } = this.node.attrs;
    if (!id || !this.partnerId) return;

    this.isLoading = true;
    this.hasFetched = true;
    this.isErrored = false;
    this.error = null;
    try {
      if (refType === BackendResourceEnum.TICKET) {
        const ticket = new Ticket({ partnerId: this.partnerId, id });
        await ticket.fetch(true);
        this.resolvedItem = ticket;
      } else if (refType === BackendResourceEnum.REPORT) {
        const report = new Report({ partnerId: this.partnerId, id });
        await report.fetch(true);
        this.resolvedItem = report;
      } else if (refType === BackendResourceEnum.VEHICLE) {
        const vehicle = new Vehicle({ partnerId: this.partnerId, id });
        await vehicle.fetch();
        this.resolvedItem = vehicle;
      } else if (refType === BackendResourceEnum.THG) {
        // `Thg.fetch` swallows its error, which would leave the preview stuck on the
        // loading spinner — go through the service so a 404 reaches the catch below.
        const thg = new Thg(await thgService.getThgByPartnerId(id, this.partnerId));
        ThgDataAccessLayer.set(thg);
        this.resolvedItem = thg;
      }
    } catch (e) {
      this.error = e;
      this.isErrored = true;
      this.resolvedItem = null;
    }
    this.isLoading = false;
  }

  navigate() {
    const { id, refType } = this.node.attrs;
    if (!id || !this.partnerId) return;

    if (refType === BackendResourceEnum.TICKET) {
      this.$router.push({ name: TicketRouteNames.TICKET_DETAIL, params: { partnerId: this.partnerId, ticketId: id } });
    } else if (refType === BackendResourceEnum.REPORT) {
      this.$router.push({ name: ReportRouteNames.REPORT_DETAIL, params: { partnerId: this.partnerId, reportId: id } });
    } else if (refType === BackendResourceEnum.VEHICLE) {
      this.$router.push({ name: "FleetVehicleDetail", params: { partnerId: this.partnerId, vehicleId: id } });
    } else if (refType === BackendResourceEnum.THG) {
      this.$router.push({ name: "ThgQuoteDetailView", params: { partnerId: this.partnerId, thgId: id } });
    }
  }
}
</script>

<style scoped>
.ref-mention-node-view {
  display: inline;
}

.ref-mention-chip {
  cursor: pointer;
}

.ref-mention-chip:hover {
  text-decoration: underline;
}
</style>
